import { replaceEmojis } from './EmojiHelper';
import { customerDisplayName } from './CustomerDisplay';
// @ts-ignore
import { sanitize } from 'dompurify';
import ChatMessageList from '@src/interfaces/ChatMessageList';
import {
	ChatTagging,
	Message,
	MessageEchoOrigin,
	MessageStatus,
	MessageType,
	MessageWabaPayload,
} from '@src/types/messages';
import { parseIntSafely } from '@src/helpers/IntegerHelper';
import ReactionList from '@src/interfaces/ReactionList';
import { generateUniqueID } from '@src/helpers/Helpers';
import { getUnixTimestamp } from '@src/helpers/DateHelper';
import { Template } from '@src/types/templates';
import { ChatAssignmentEvent } from '@src/types/chatAssignment';

// WhatsApp renders a link preview only for URLs starting with http:// or
// https:// (and only for the first URL in the body)
export const containsPreviewableURL = (text: string): boolean => {
	return /https?:\/\/\S+/i.test(text);
};

export const prepareMessageList = (
	messages: Message[],
	existingMessages: ChatMessageList = {}
) => {
	const result: ChatMessageList = {};
	messages.forEach((message) => {
		// Ignore messages without timestamp
		// Ignore messages that are already loaded
		if (
			(parseIntSafely(message.waba_payload?.timestamp) ?? -1) > 0 &&
			!(message.waba_payload && message.waba_payload.id in existingMessages)
		) {
			// Consider switching to getchat id only
			const messageKey =
				message.waba_payload?.id ?? generateMessageInternalId(message.id);
			// is_failed is client-side only (set by the websocket status
			// handler); REST responses carry the errors in waba_payload
			// instead, so derive it — otherwise failed messages regress from
			// the error icon to the pending clock icon after a page refresh.
			// Outgoing only, mirroring the websocket path: status events (and
			// with them is_failed) exist only for business-sent messages.
			const isFailed =
				message.is_failed ||
				(message.from_us && (message.waba_payload?.errors?.length ?? 0) > 0);
			result[messageKey] = isFailed ? { ...message, is_failed: true } : message;
		}
	});

	return result;
};

export const prepareReactions = (messages: ChatMessageList) => {
	let reactions: ReactionList = {};
	Object.values(messages)
		.filter((item) => item.waba_payload?.type === MessageType.reaction)
		.forEach((item) => {
			const message = item;
			const parentMessageId = message.waba_payload?.reaction?.message_id;
			if (parentMessageId) {
				if (!reactions[parentMessageId]) {
					reactions[parentMessageId] = [message];
				} else {
					reactions[parentMessageId].push(message);
				}
			}
		});

	return reactions;
};

export const generateEmptyMessage = () => {
	const message: Message = {
		id: '',
		customer_wa_id: '',
		from_us: true,
		received: true,
		tags: [],
		chat_tags: [],
		is_failed: false,
		waba_payload: {
			id: '',
			type: MessageType.none,
			timestamp: '-1',
		},
	};
	return message;
};

export const fromIncomingMessageWabaPayload = (
	wabaPayload: MessageWabaPayload
): Message => {
	return {
		waba_payload: wabaPayload,
		id: wabaPayload.id,
		customer_wa_id:
			wabaPayload.from ?? wabaPayload.from_user_id ?? wabaPayload.wa_id ?? '',
		from_us: false,
		received: true,
		tags: [],
		chat_tags: [],
		is_failed: false,
	} as Message;
};

export const fromAssignmentEvent = (
	assignmentEvent: ChatAssignmentEvent
): Message => {
	const id =
		'assignmentEvent_' + assignmentEvent.timestamp + '_' + generateUniqueID();
	return {
		...generateEmptyMessage(),
		id,
		customer_wa_id: assignmentEvent.wa_id,
		assignment_event: assignmentEvent,
		waba_payload: {
			id,
			type: MessageType.none,
			timestamp: assignmentEvent.timestamp?.toString(),
		},
	};
};

export const fromTemplate = (template: Template): Message => {
	return {
		...generateEmptyMessage(),
		waba_payload: {
			id: generateUniqueID(),
			type: MessageType.interactive,
			template: template,
			timestamp: getUnixTimestamp().toString(),
		},
	};
};

export const fromInteractive = (interactive: any): Message => {
	return {
		...generateEmptyMessage(),
		waba_payload: {
			id: generateUniqueID(),
			type: MessageType.interactive,
			interactive: interactive,
			timestamp: getUnixTimestamp().toString(),
		},
	};
};

export const fromTaggingEvent = (taggingEvent: ChatTagging): Message => {
	const id =
		'taggingEvent_' + taggingEvent.timestamp + '_' + generateUniqueID();
	return {
		...generateEmptyMessage(),
		id,
		customer_wa_id: taggingEvent.chat,
		tagging_event: taggingEvent,
		waba_payload: {
			id,
			type: MessageType.none,
			timestamp: taggingEvent.timestamp?.toString(),
		},
	};
};

export const getMessageTimestamp = (message: Message | undefined) =>
	message ? parseIntSafely(message.waba_payload?.timestamp) : undefined;

export const getSenderName = (message: Message) => {
	if (message.sender) {
		const firstName = message.sender.first_name;
		const lastName = message.sender.last_name;

		if (firstName || lastName) {
			return firstName + (lastName ? ' ' + lastName : '');
		} else {
			return message.sender?.username;
		}
	}

	return !message.from_us ? customerDisplayName(message.contact?.waba_payload?.profile?.name) : 'Us';
};

// echo_origin only exists on messages delivered via echo webhook events; a
// REST fetch of the same message carries only the is_echo marker in its
// stored payload (API echoes only — the backend does not persist the origin
// of SMB echoes), so fall back to it to keep the label after a page refresh
export const getMessageEchoOrigin = (
	message: Message
): MessageEchoOrigin | undefined =>
	message.echo_origin ??
	(message.waba_payload?.is_echo ? MessageEchoOrigin.api : undefined);

// Untranslated label; callers are expected to pass it through t()
export const getEchoOriginLabel = (message: Message) => {
	const echoOrigin = getMessageEchoOrigin(message);
	if (!echoOrigin) return undefined;
	return echoOrigin === MessageEchoOrigin.smb
		? 'via WhatsApp Business App'
		: 'via API';
};

export const getUniqueSender = (message: Message) => {
	const echoOrigin = getMessageEchoOrigin(message);
	return (
		message.sender?.username ??
		// ':' cannot appear in usernames, so echo groups can never collide with one
		(echoOrigin ? 'echo:' + echoOrigin : message.waba_payload?.from)
	);
};

export const generateMessageInternalId = (getChatId: string) => {
	return 'getchatId_' + getChatId;
};

const KNOWN_MESSAGE_TYPES = new Set<string>(Object.values(MessageType));

export const isUnsupportedMessageType = (type: string | undefined) =>
	type !== undefined && !KNOWN_MESSAGE_TYPES.has(type);

export const hasAnyStatus = (message: Message) => {
	return (
		(message.waba_statuses?.sent ?? 0) > 0 ||
		(message.waba_statuses?.delivered ?? 0) > 0 ||
		(message.waba_statuses?.read ?? 0) > 0
	);
};

export const getStatus = (message: Message) => {
	if (message.waba_statuses?.read) {
		return MessageStatus.read;
	}

	if (message.waba_statuses?.delivered) {
		return MessageStatus.delivered;
	}

	if (message?.waba_statuses?.sent) {
		return MessageStatus.sent;
	}

	return MessageStatus.pending;
};

export const isRead = (message: Message) =>
	getStatus(message) === MessageStatus.read;

export const isPending = (message: Message) =>
	getStatus(message) === MessageStatus.pending;

export const isJustSent = (message: Message) =>
	getStatus(message) === MessageStatus.sent;

export const isDeliveredOrRead = (message: Message) => {
	const status = getStatus(message);
	return status === MessageStatus.delivered || status === MessageStatus.read;
};

export const getMessageCaption = (message: Message | undefined) => {
	if (message) {
		const caption =
			message.waba_payload?.image?.caption ??
			message.waba_payload?.video?.caption ??
			message.waba_payload?.audio?.caption ??
			message.waba_payload?.document?.caption;
		return caption ? sanitize(caption) : undefined;
	}
};

export const getMessageMimeType = (message: Message) => {
	const payload = message.waba_payload;
	if (payload) {
		return (
			payload.image?.mime_type ??
			payload.video?.mime_type ??
			payload.audio?.mime_type ??
			payload.voice?.mime_type ??
			payload.document?.mime_type
		);
	}
};

export const hasAnyAudio = (message: Message) => {
	return (
		message.waba_payload?.voice?.id !== undefined ||
		message.waba_payload?.audio?.id !== undefined
	);
};

export const hasMediaToPreview = (message: Message) => {
	return (
		message.waba_payload?.image !== undefined ||
		message.waba_payload?.video !== undefined ||
		getHeaderFileLink(message, 'image') ||
		getHeaderFileLink(message, 'video')
	);
};

export const getHeaderFileLink = (message: Message, type: string) => {
	try {
		if (message.waba_payload?.type === MessageType.template) {
			if (message.waba_payload?.template?.components) {
				for (
					let i = 0;
					i < message.waba_payload.template.components.length;
					i++
				) {
					const component = message.waba_payload.template.components[i];

					if (
						component &&
						component.type.toLowerCase() === 'header' &&
						component.parameters?.length
					) {
						for (let j = 0; j < component.parameters.length; j++) {
							const param = component.parameters[j];

							if (param.type === type) {
								return param[type]?.link;
							}
						}
					}
				}
			}
		}
	} catch (error) {
		console.error(error);
	}

	return undefined;
};

export const generateReferralImageLink = (message: Message) => {
	if (message.waba_payload?.referral?.image) {
		return (
			message.waba_payload.referral.image.link ??
			generateMediaLink(message.waba_payload.referral.image.id)
		);
	} else if (message.waba_payload?.referral?.image_url) {
		return message.waba_payload?.referral.image_url;
	}
};

export const generateReferralVideoLink = (message: Message) => {
	if (message.waba_payload?.referral?.video) {
		return (
			message.waba_payload.referral.video.link ??
			generateMediaLink(message.waba_payload.referral?.video?.id)
		);
	} else if (message.waba_payload?.referral?.video_url) {
		return message.waba_payload.referral.video_url;
	}
};

const generateMediaLink = (id: string | number | undefined) => {
	return id ? `${window.config.API_BASE_URL}media/${id}` : undefined;
};

// Stored WhatsApp attachments always use the authenticated local copy.
export const attachmentLink = (media?: {id?: string | number; link?: string}) => {
	if (media?.id) return generateMediaLink(media.id);
	if (window.config?.APP_ENV_NAME !== 'cvparc-shadow') return media?.link;
	// Newly uploaded files may have a local link before their ID is assigned.
	if (media?.link && /^\/api\/v1\/media\/[A-Za-z0-9_-]+\/?$/.test(media.link)) return media.link;
	return undefined;
};

export const generateImageLink = (
	message: Message,
	includeTemplateHeader = false
) => {
	return (
		attachmentLink(message.waba_payload?.image) ??
		(includeTemplateHeader ? getHeaderFileLink(message, 'image') : undefined)
	);
};

export const generateVideoLink = (
	message: Message,
	includeTemplateHeaderOrReferral = false
) => {
	return (
		attachmentLink(message.waba_payload?.video) ??
		(includeTemplateHeaderOrReferral
			? getHeaderFileLink(message, 'video') ??
			  generateReferralVideoLink(message)
			: undefined)
	);
};

export const generateAudioLink = (message: Message) => {
	return (
		attachmentLink(message.waba_payload?.audio)
	);
};

export const generateDocumentLink = (message: Message) => {
	return (
		attachmentLink(message.waba_payload?.document)
	);
};

export const generateVoiceLink = (message: Message) => {
	return (
		attachmentLink(message.waba_payload?.voice)
	);
};

export const generateStickerLink = (message: Message) => {
	return (
		attachmentLink(message.waba_payload?.sticker)
	);
};

export const canRetry = (message: Message) => {
	let result = false;

	if (
		message.waba_payload?.errors &&
		Array.isArray(message.waba_payload.errors)
	) {
		for (let i = 0; i < message.waba_payload.errors.length; i++) {
			if (
				ERR_CODES_FOR_RETRY.includes(
					message.waba_payload.errors[i]?.['code'] ?? 0
				)
			) {
				result = true;
				break;
			}
		}
	}

	return result;
};

export const formatMessage = (message: string | undefined) => {
	if (!message) return;

	let formatted: string | undefined = message.replaceAll('\n', '<br/>');
	formatted = sanitize(formatted);
	formatted = replaceEmojis(formatted ?? '');

	return formatted;
};

export const generateMessagePreview = (payload: any) => {
	const messageType = payload?.type;
	if (messageType === 'text') {
		return payload?.text?.body ?? '';
	} else if (messageType === 'template') {
		return 'Template: ' + (payload?.template?.name ?? '');
	} else {
		return messageType;
	}
};

export const ERR_CODES_FOR_RETRY = [
	400, 410, 429, 430, 432, 433, 470, 471, 500, 1000, 1005, 1011, 1015, 1016,
	1018, 1023, 1024, 1026, 1031,
];
